import { useState, useEffect, useCallback } from "react";
import {
    Sparkles, Phone, CalendarClock, FileText, ArrowUpCircle, UserPlus, Leaf,
    XCircle, Check, X, Clipboard, RefreshCw,
} from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { AI_SUGGESTIONS_URL, AI_SUGGESTION_UPDATE } from "../../constants/api";
import ToastMsg from "../common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";
import dayjs from "dayjs";

// Mirrors LeadAISuggestion.ACTION_TYPES on the backend
const ACTIONS = {
    call_now:        { label: "Call Now",        icon: Phone },
    reschedule_demo: { label: "Reschedule Demo", icon: CalendarClock },
    send_proposal:   { label: "Send Proposal",   icon: FileText },
    escalate:        { label: "Escalate",        icon: ArrowUpCircle },
    reassign:        { label: "Reassign",        icon: UserPlus },
    nurture:         { label: "Nurture",         icon: Leaf },
    drop:            { label: "Drop",            icon: XCircle },
};

const priorityLabel = (p) => (p >= 80 ? "Urgent" : p >= 50 ? "This week" : "Low");

/**
 * AI next-action panel for a single lead, shown on the lead detail page.
 * Reads the newest suggestion (any status) written by the local AI worker
 * (`manage.py generate_lead_suggestions`). Renders nothing when the worker
 * has not analysed this lead yet, so it never adds an empty card.
 */
const LeadAISuggestionPanel = ({ customerId }) => {
    const { fetch, update } = useApiMethods();
    const [row,      setRow]      = useState(null);
    const [loading,  setLoading]  = useState(false);
    const [saving,   setSaving]   = useState(false);
    const [showScript, setShowScript] = useState(false);

    const load = useCallback(() => {
        if (!customerId) return;
        setLoading(true);
        fetch(`${AI_SUGGESTIONS_URL}?customer_id=${customerId}&status=all`)
            .then(res => {
                const list = Array.isArray(res?.data) ? res.data : [];
                setRow(list[0] || null);   // API orders by priority then newest
            })
            .catch(() => setRow(null))
            .finally(() => setLoading(false));
    }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const setStatus = (newStatus) => {
        setSaving(true);
        update(AI_SUGGESTION_UPDATE(row.id), { status: newStatus })
            .then(res => {
                if (res?.status === "success") {
                    ToastMsg("success", `Marked ${newStatus}`);
                    setRow(prev => ({ ...prev, status: newStatus }));
                } else {
                    ToastMsg("error", res?.message || "Failed to update");
                }
            })
            .catch(() => ToastMsg("error", "Failed to update"))
            .finally(() => setSaving(false));
    };

    const copyScript = () => {
        navigator.clipboard?.writeText(row.suggested_script)
            .then(() => ToastMsg("success", "Script copied"))
            .catch(() => ToastMsg("error", "Could not copy"));
    };

    if (loading && !row) {
        return (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 flex items-center gap-2">
                <RefreshCw className="h-3 w-3 animate-spin text-gray-400" />
                <span className="text-xs text-gray-500 dark:text-gray-400">Loading AI suggestion…</span>
            </div>
        );
    }

    // Nothing analysed for this lead yet — stay out of the way entirely.
    if (!row) return null;

    const meta = ACTIONS[row.action_type] || { label: row.action_type, icon: Sparkles };
    const Icon = meta.icon;
    const urgent = row.priority >= 80;
    const settled = row.status === "done" || row.status === "dismissed";

    return (
        <div className={cn(
            "rounded-xl border overflow-hidden",
            urgent
                ? "border-rose-300 dark:border-rose-800 bg-rose-50/60 dark:bg-rose-900/15"
                : "border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-900/15"
        )}>
            <div className="px-3 py-2 flex items-center justify-between gap-2 border-b border-black/5 dark:border-white/5">
                <span className={cn(
                    "flex items-center gap-1.5 text-[11px] font-semibold",
                    urgent ? "text-rose-800 dark:text-rose-200" : "text-indigo-900 dark:text-indigo-200"
                )}>
                    <Sparkles className="h-3.5 w-3.5" />
                    AI Next Action
                </span>
                <div className="flex items-center gap-1.5">
                    <span className={cn(
                        "px-2 py-0.5 rounded-full text-[10px] font-medium tabular-nums",
                        urgent
                            ? "bg-rose-200/70 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200"
                            : "bg-indigo-200/70 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200"
                    )}>
                        {priorityLabel(row.priority)} · {row.priority}
                    </span>
                    <button onClick={load} title="Refresh"
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} />
                    </button>
                </div>
            </div>

            <div className="px-3 py-2.5">
                <div className="flex items-center gap-1.5 mb-1.5">
                    <Icon className={cn("h-3.5 w-3.5", urgent ? "text-rose-600 dark:text-rose-300" : "text-indigo-600 dark:text-indigo-300")} />
                    <span className="text-xs font-semibold text-gray-900 dark:text-white">{meta.label}</span>
                    {settled && (
                        <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                            {row.status}
                        </span>
                    )}
                </div>

                {row.reasoning && (
                    <p className="text-[11px] leading-relaxed text-gray-700 dark:text-gray-300">
                        {row.reasoning}
                    </p>
                )}

                {row.suggested_script && (
                    <>
                        <button onClick={() => setShowScript(s => !s)}
                            className="mt-2 text-[10px] font-medium text-indigo-600 dark:text-indigo-400 hover:underline">
                            {showScript ? "Hide" : "Show"} suggested opener
                        </button>
                        {showScript && (
                            <div className="mt-1.5 rounded-lg bg-white/70 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 p-2">
                                <p className="text-[11px] text-gray-700 dark:text-gray-200 whitespace-pre-line">
                                    {row.suggested_script}
                                </p>
                                <button onClick={copyScript}
                                    className="mt-1.5 text-[10px] font-medium text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 flex items-center gap-1">
                                    <Clipboard className="h-3 w-3" /> Copy
                                </button>
                            </div>
                        )}
                    </>
                )}

                {/* Lifecycle: pending → accepted → done, or dismissed */}
                {!settled && (
                    <div className="mt-2.5 flex items-center gap-1.5">
                        {row.status === "pending" && (
                            <>
                                <button disabled={saving} onClick={() => setStatus("accepted")}
                                    className="h-6 px-2 rounded-md text-[10px] font-medium bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white flex items-center gap-1">
                                    <Check className="h-3 w-3" /> Accept
                                </button>
                                <button disabled={saving} onClick={() => setStatus("dismissed")}
                                    className="h-6 px-2 rounded-md text-[10px] font-medium border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center gap-1">
                                    <X className="h-3 w-3" /> Dismiss
                                </button>
                            </>
                        )}
                        {row.status === "accepted" && (
                            <button disabled={saving} onClick={() => setStatus("done")}
                                className="h-6 px-2 rounded-md text-[10px] font-medium bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white flex items-center gap-1">
                                <Check className="h-3 w-3" /> Mark done
                            </button>
                        )}
                    </div>
                )}

                {row.generated_at && (
                    <p className="mt-2 text-[10px] text-gray-400 dark:text-gray-500">
                        Generated {dayjs(row.generated_at).format("DD MMM YYYY, hh:mm A")}
                        {row.user_name ? ` · for ${row.user_name}` : ""}
                    </p>
                )}
            </div>
        </div>
    );
};

export default LeadAISuggestionPanel;
